import React from 'react';
import {
  BookMarked,
  Star,
  GitFork,
  Eye,
  FileText,
  CheckCircle2,
  Lock,
  History,
} from 'lucide-react';

import {
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Code,
  Container,
  Group,
  Image,
  Paper,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

function MetricButton({ icon, label, count }) {
  return (
    <Button variant="default" size="xs" leftSection={icon}>
      {label}
      <Badge variant="light" color="gray" ml="xs">
        {count}
      </Badge>
    </Button>
  );
}

function Shield({ icon, left, right, color }) {
  return (
    <Group gap={0} wrap="nowrap">
      <Badge variant="filled" color="dark" leftSection={icon} radius="sm">
        {left}
      </Badge>
      <Badge variant="filled" color={color} radius="sm">
        {right}
      </Badge>
    </Group>
  );
}

export default function ReadmeIntegration() {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');

  return (
    <Box mih="100vh" py={32}>
      <Stack gap={0}>
        <Paper
          radius={0}
          withBorder
          p="md"
          bg={colorScheme === 'dark' ? 'dark.8' : 'gray.0'}
        >
          <Container size="xl">
            <Group justify="space-between" align="center" wrap="wrap">
              <Group gap="xs">
                <BookMarked size={18} />
                <Anchor c="blue" fz="lg" underline="never">
                  acme-corp
                </Anchor>
                <Text c="dimmed" fz="lg">
                  /
                </Text>
                <Anchor c="blue" fw={600} fz="lg" underline="never">
                  super-awesome-ai
                </Anchor>
                <Badge variant="outline" color="gray" radius="xl">
                  {t('Public')}
                </Badge>
              </Group>

              <Group gap="xs">
                <MetricButton icon={<Eye size={14} />} label={t('Watch')} count="142" />
                <MetricButton icon={<GitFork size={14} />} label={t('Fork')} count="89" />
                <MetricButton icon={<Star size={14} />} label={t('Star')} count="1.2k" />
              </Group>
            </Group>
          </Container>
        </Paper>

        <Container size="lg" py="xl">
          <Stack gap="xl">
            <Paper withBorder radius="md">
              <Group p="sm" px="md" justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  <Avatar src="https://github.com/identicons/jason.png" size={24} radius="xl" />
                  <Text fw={600} fz="sm">
                    jason-dev
                  </Text>
                  <Text c="dimmed" fz="sm" truncate>
                    {t('Update README.md with Timestamp GIT badges')}
                  </Text>
                </Group>
                <Group gap="md" wrap="nowrap">
                  <Code>8f92a1c</Code>
                  <Text c="dimmed" fz="sm">
                    {t('2 hours ago')}
                  </Text>
                </Group>
              </Group>
            </Paper>

            <Paper withBorder radius="md">
              <Group p="sm" px="md" gap="xs">
                <FileText size={16} />
                <Text fw={600} fz="sm">
                  README.md
                </Text>
              </Group>

              <Stack p="xl" gap="lg">
                <Title order={1}>{t('Super Awesome AI')}</Title>

                <Group gap="xs" wrap="wrap">
                  <Shield
                    icon={<CheckCircle2 size={12} />}
                    left="Timestamp GIT"
                    right={t('Protected')}
                    color="teal"
                  />
                  <Shield
                    icon={<History size={12} />}
                    left={t('Prior Art')}
                    right={t('1,245 Commits')}
                    color="blue"
                  />
                  <Shield
                    icon={<Lock size={12} />}
                    left={t('Latest Anchor')}
                    right={t('Block #834102')}
                    color="orange"
                  />
                  <Image
                    src="https://img.shields.io/badge/license-MIT-green.svg"
                    alt="License"
                    h={22}
                    w="auto"
                    fit="contain"
                  />
                  <Image
                    src="https://img.shields.io/badge/python-3.10%2B-blue.svg"
                    alt="Python"
                    h={22}
                    w="auto"
                    fit="contain"
                  />
                </Group>

                <Text>
                  {t('Welcome to the official repository for')}{' '}
                  <Text component="span" fw={700}>
                    Super Awesome AI
                  </Text>
                  .{' '}
                  {t('This project provides next-generation machine learning tools for edge devices.')}
                </Text>

                <Paper withBorder p="md" radius="md" bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}>
                  <Text fz="sm" lh={1.6}>
                    <Text component="span" fw={700}>
                      {t('Intellectual Property Notice:')}{' '}
                    </Text>
                    {t('All commits in this repository are cryptographically timestamped and anchored to the Bitcoin blockchain using ')}
                    <Anchor href="#" underline="never">
                      Timestamp GIT
                    </Anchor>
                    .{' '}
                    {t('This provides mathematically undeniable proof of prior art and authorship for all code and ideas contained within.')}
                  </Text>
                </Paper>

                <Title order={2}>{t('Installation')}</Title>
                <Text>{t('You can install the package via pip:')}</Text>
                <Code block>pip install super-awesome-ai</Code>

                <Title order={2}>{t('Usage')}</Title>
                <Text>
                  {t('Here is a quick example of how to initialize the model in your application:')}
                </Text>
                <Code block>{`import awesome_ai

model = awesome_ai.init("v2-edge")
result = model.predict(data)
print(result)`}</Code>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Stack>
    </Box>
  );
}
